import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { inject, observer } from "mobx-react";

import CreateNode from "../CreateNode";
import AbVariants from "./AbVariants";
import StepIndicator from "../StepIndicator";

import { confirmDialog } from "primereact/confirmdialog";

import CardUI from "@UI/CardUI";
import ButtonUI from "@UI/ButtonUI";
import TagUi from "@UI/TagUi";

import { useClickOutside } from "@hook";
import { differ, changeValByPath, getValByPath, getStatusTag, validateRequiredFields } from "@utils";

import { btnsConfig } from "./config";

import moment from "moment";

import { pageTitles } from "@content";

import "./style.scss";

const DefaultDetail = inject("mainStore")(
    observer(
        forwardRef(
            (
                {
                    mainStore,
                    title = "Редактировать элемент",
                    inputs = [],
                    dataKey,
                    apiUrl = "",
                    backUrl = false,
                    backEnabled = true,
                    queryForApi = false,
                    page = "",
                    abTest = false,
                    setStep,
                    step,
                    funcAfterSave = null, // выполнить функцию после сохранения (создания) записи в базе (POST)
                    countOfSteps = 5,
                },
                ref,
            ) => {
                const paramsMatch = useParams();
                const location = useLocation();

                const detailData = mainStore.getMainStore("detailData") || {};
                const detaiDeflData = mainStore.getMainStore("detaiDeflData") || {};
                const eridToRegenerate = mainStore.getMainStore("eridToRegenerate");
                const siteCodeToData = mainStore.getMainStore("siteCodeToData") || {};
                const disableButtonPublish = mainStore.getMainStore("disableButtonPublish");

                const siteCode = siteCodeToData && paramsMatch && paramsMatch.secondParam && siteCodeToData[paramsMatch.secondParam] ? siteCodeToData[paramsMatch.secondParam]._id : false;

                const isAdd = paramsMatch.secondParam === "add";
                const isCompanyAdd = isAdd && paramsMatch.firstParam === "companies";
                const isBucketsAdd = isAdd && paramsMatch.firstParam === "buckets-add";

                const isDraft = detailData.draft;
                const isActive = detailData.active;
                const isArchived = detailData.archived;
                const isScroll = location?.state?.scrollORD;

                const navigation = useNavigate();
                const refDetail = useRef(null);

                const [triggerConfirm, setTriggerConfirm] = useState(true);
                const [stateInputs, setStateInputs] = useState([]);
                const [errorArr, setErrorArr] = useState([]);

                const errorSet = useMemo(() => new Set(errorArr), [errorArr]);

                const diff = differ(detaiDeflData, detailData);
                const hasDiff = Object.keys(diff).length > 0;
                const prevEridDiffSignatureRef = useRef("");

                const eridFields = useMemo(() => {
                    const collectEridFields = (inputObj, prefix = "") => {
                        const res = {};
                        if (!inputObj) return res;

                        Object.entries(inputObj).forEach(([key, val]) => {
                            const fullKey = prefix ? `${prefix}.${key}` : key;
                            const regenType = val?.eridToRegenerate;

                            if (regenType === "required" || regenType === "optional") {
                                res[fullKey] = regenType;
                            }

                            if (val?.inputs) {
                                if (Array.isArray(val.inputs)) {
                                    val.inputs.forEach((group) => Object.assign(res, collectEridFields(group, fullKey)));
                                } else {
                                    Object.assign(res, collectEridFields(val.inputs, fullKey));
                                }
                            }
                        });

                        return res;
                    };

                    return (inputs || []).reduce((acc, item) => {
                        if (item?.inputs) Object.assign(acc, collectEridFields(item.inputs));
                        return acc;
                    }, {});
                }, [inputs]);

                const getEridDiffInfo = (diff, eridFields) => {
                    if (!diff || typeof diff !== "object" || !Object.keys(diff).length) {
                        return { signature: "", level: null };
                    }
                    if (!eridFields || typeof eridFields !== "object" || !Object.keys(eridFields).length) {
                        return { signature: "", level: null };
                    }

                    const relevant = [];

                    for (const path of Object.keys(eridFields)) {
                        const value = getValByPath(path, diff);

                        if (value !== undefined) relevant.push([path, value]);
                    }

                    if (!relevant.length) return { signature: "", level: null };

                    relevant.sort(([a], [b]) => a.localeCompare(b));

                    return {
                        signature: JSON.stringify(relevant),
                        level: relevant.some(([path]) => eridFields[path] === "required") ? "required" : "optional",
                    };
                };

                const eridDiffInfo = useMemo(() => getEridDiffInfo(diff, eridFields), [diff, eridFields]);

                const stepperData = useMemo(
                    () =>
                        new Array(countOfSteps).fill(null).reduce(
                            (acc, _, idx) => {
                                acc.stepsArr.push(idx + 1);
                                acc.disableStep.push({ disable: true, isStep: false });
                                return acc;
                            },
                            { stepsArr: [], disableStep: [] },
                        ),
                    [countOfSteps],
                );

                const clickTrigger = triggerConfirm && hasDiff;
                useClickOutside(clickTrigger, refDetail, (e) => handleConfirm(e));

                useImperativeHandle(
                    ref,
                    () => ({
                        handleSaveAndUpdate,
                        disabledUpdataBtn,
                    }),
                    [detailData, detaiDeflData, inputs, errorArr, hasDiff],
                );

                useEffect(() => {
                    if (!Object.keys(detailData).length && !isAdd) {
                        getData();
                    }

                    return () => {
                        if (paramsMatch.firstParam == "companies" && paramsMatch.secondParam !== "add") {
                            mainStore.setMainStore("detailData", {});
                            mainStore.setMainStore("detaiDeflData", {});
                            mainStore.setMainStore("eridToRegenerate", null);
                        }
                    };
                }, [paramsMatch.secondParam]);

                useEffect(() => {
                    setStateInputs(inputs || []);
                }, [inputs]);

                useEffect(() => {
                    if (detailData?.vendor?.vendorInfo?.workViaAgency || isAdd || !detailData.erid) {
                        mainStore.setMainStore("eridToRegenerate", null);
                        return;
                    }
                    if (eridDiffInfo.signature === prevEridDiffSignatureRef.current) return;

                    prevEridDiffSignatureRef.current = eridDiffInfo.signature;

                    if (eridDiffInfo.level && eridToRegenerate !== eridDiffInfo.level) {
                        mainStore.setMainStore("eridToRegenerate", eridDiffInfo.level);
                        if (eridDiffInfo.level === "required") {
                            mainStore.setMainStore("toast", { severity: "warn", summary: "Перед публикацией необходимо перегенерировать erid" });
                        }
                    } else if (!eridDiffInfo.level && eridToRegenerate) {
                        mainStore.setMainStore("eridToRegenerate", null);
                    }
                }, [eridDiffInfo, eridToRegenerate]);

                useEffect(() => {
                    const baseTitle = pageTitles[paramsMatch.firstParam] || "CMS RIS PROMO";

                    if (paramsMatch.secondParam) document.title = `${baseTitle}${detailData.title || detailData.name ? "_" + (detailData.title || detailData.name).replace(/<\/?[^>]+(>|$)/g, "").replace(/\u00A0|&nbsp;/g, " ") : ""}`;
                }, [detailData, paramsMatch.firstParam, paramsMatch.secondParam]);

                const handleConfirm = (e) => {
                    e.stopPropagation();
                    setTriggerConfirm(false);

                    const confData = isDraft || isAdd ? "draft" : "save";
                    const obj = {
                        draft: { title: "Есть изменения, добавить в черновик?", func: () => handleDraft(e) },
                        save: {
                            title: "Есть изменения, сохранить изменения?",
                            func: () => handleUpdate({ draft: false, active: true, archived: false }),
                        },
                    };

                    confirmDialog({
                        message: obj[confData].title,
                        header: "Внимание!",
                        icon: "pi pi-exclamation-triangle",
                        defaultFocus: "accept",
                        acceptLabel: "Да",
                        rejectLabel: "Нет",
                        onHide: () => {
                            setTriggerConfirm(true); // бездействие при закрытии окна на крестик
                        },
                        accept: () => {
                            setTriggerConfirm(true);
                            obj[confData].func();
                        },
                        reject: () => {
                            setTriggerConfirm(true);
                            e.target.click();
                        },
                    });
                };

                const getData = async (id) => {
                    if (!apiUrl && !id) return;

                    const resp = await mainStore.getByUrl(id ? `/promo/offers/${id}` : apiUrl);
                    if (resp && resp.success) {
                        modifyAfterGetData(resp.data, paramsMatch, mainStore);
                    }
                };

                const setStateData = useCallback(
                    (path, value) => {
                        const generatePath = path.reduce((acc, row, index) => acc + (index === 0 ? row : "." + row), "detailData.");
                        mainStore.setMainStore(generatePath, value);
                    },
                    [mainStore],
                );

                const setStateDataMulti = useCallback(
                    (array) => {
                        let currentData = { ...detailData };
                        array.forEach((item) => {
                            const generatePath = item.path.reduce((acc, row, index) => acc + (index === 0 ? row : "." + row), "");
                            currentData = changeValByPath(generatePath, item.value, currentData);
                        });

                        mainStore.setMainStore("detailData", currentData);
                    },
                    [detailData, mainStore],
                );

                const confirmArchive = (params) => {
                    confirmDialog({
                        header: "Архивировать?",
                        message: `Вы уверены что хотите архивировать данный элемент?`,
                        icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Да",
                        rejectLabel: "Нет",
                        accept: () => {
                            handleUpdate(params);
                        },
                    });
                };

                const confirmDraft = () => {
                    confirmDialog({
                        header: "Сохранить черновик?",
                        message: `Проект не будет запущен, но останется в черновиках`,
                        icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Да",
                        rejectLabel: "Нет",
                        accept: (e) => {
                            handleDraft(e);
                        },
                    });
                };

                const confirmPause = (data) => {
                    confirmDialog({
                        header: detailData.active ? "Поставить паузу?" : "Снять с паузы?",
                        message: detailData.active ? "Проект будет находится на паузе" : "Проект будет снят с паузы",
                        icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Да",
                        rejectLabel: "Нет",
                        accept: () => {
                            handlePause(data);
                        },
                    });
                };

                const confirmPublish = (params) => {
                    confirmDialog({
                        header: "Вы уверены, что хотите опубликовать оффер?",
                        message: "Проверьте все ли данные указаны верно",
                        // icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Опубликовать",
                        rejectLabel: "Отмена",
                        accept: () => {
                            handleSave(params);
                        },
                    });
                };

                const confirmPublishChanges = (params) => {
                    confirmDialog({
                        header: "Вы уверены, что хотите опубликовать оффер?",
                        message: "Проверьте все ли данные указаны верно, вы внесли изменения",
                        // icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Опубликовать",
                        rejectLabel: "Отмена",
                        accept: () => {
                            handleSave(params);
                        },
                    });
                };

                const confirmPublishUpdata = (params) => {
                    confirmDialog({
                        header: "Вы уверены, что хотите опубликовать оффер?",
                        message: "Проверьте все ли данные указаны верно,  вы внесли изменения",
                        // icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Опубликовать",
                        rejectLabel: "Отмена",
                        accept: () => {
                            handleUpdate(params);
                        },
                    });
                };

                const confirmPublishUpdataChanges = (params) => {
                    confirmDialog({
                        header: "Вы уверены, что хотите опубликовать оффер?",
                        message: "Проверьте все ли данные указаны верно",
                        // icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Опубликовать",
                        rejectLabel: "Отмена",
                        accept: () => {
                            handleUpdate(params);
                        },
                    });
                };

                const confirmEridGenerate = (params, isSave = false) => {
                    confirmDialog({
                        header: `${isSave ? "Сохранить" : "Запустить"} оффер без изменение ERID ?`,
                        message: "Вы изменили поля при которых рекомендовано изменить ERID",
                        acceptLabel: "Сгенерировать ERID",
                        rejectLabel: `${isSave ? "Сохранить" : "Опубликовать"} без изменений`,
                        reject: () => {
                            handleUpdate(params);
                        },
                    });
                };

                const handleDraft = (e) => {
                    isAdd ? handleSave(e, { draft: true, active: false }) : handleUpdate();
                };

                const handlePause = (data) => {
                    if (paramsMatch.firstParam === "companies") {
                        const currentDate = moment();
                        const oneWeekLater = currentDate.clone().add(7, "days");

                        const result = detailData.ratesArray.reduce((acc, rate) => {
                            const dateStart = moment(rate.dateStart);
                            const dateEnd = rate.dateEnd ? moment(rate.dateEnd) : null;

                            if (dateStart.isAfter(currentDate)) {
                                return acc;
                            }

                            if (!dateEnd || (dateEnd && dateEnd.isAfter(oneWeekLater))) {
                                acc.push({
                                    ...rate,
                                    dateEnd: oneWeekLater.toISOString(),
                                });
                            } else {
                                acc.push({
                                    ...rate,
                                });
                            }
                            return acc;
                        }, []);

                        data.ratesArray = result;
                    }

                    handleUpdate({
                        active: !detailData.active,
                        draft: false,
                        ...data,
                    });
                };

                const handleUpdate = async (additionalData = {}) => {
                    let data = { ...detailData, ...additionalData };
                    if (paramsMatch.firstParam === "companies") data = modifyDataToUpdate(data); // удаляет ненужные ключи в кампаниях
                    if (paramsMatch.firstParam === "offers" && data?.vendorInfo?.workViaAgency) {
                        data.ord = `Реклама. Рекламодатель ${data.vendorInfo?.fullOpf || ""}, ИНН ${data.vendorInfo?.INN || ""}`;
                    }

                    const resp = await mainStore.putByUrl(`${apiUrl}`, data);
                    if (resp && resp.success) {
                        if (backEnabled) {
                            handleBack(resp.data);
                        } else {
                            // если обратно не нужно выходить в список обновляем состояние всех полей после записи
                            getData(); // перезапрос данных, чтобы detaiDeflData снова совпал с detailData
                        }

                        mainStore.setMainStore("toast", {
                            severity: "success",
                            summary: "Изменено",
                            detail: `Вы изменили ${title}`,
                        });
                        return;
                    }
                    if ((!resp.success && resp.msg) || resp.data) {
                        mainStore.setMainStore("toast", {
                            severity: "warn",
                            summary: "Ошибка",
                            detail: resp.msg || resp.data,
                        });
                        return;
                    }
                    mainStore.setMainStore("toast", {
                        severity: "warn",
                        summary: "Ошибка",
                        detail: "Что-то пошло не так",
                    });
                };

                const handleBack = (data = null) => {
                    mainStore.setMainStore("loader", true);
                    const url = backUrl ? backUrl : (isCompanyAdd || isBucketsAdd) && data?._id ? `/${paramsMatch.firstParam}/${data._id}` : `/${paramsMatch.firstParam}`;
                    mainStore.setMainStore("defaultOffer", false);
                    mainStore.setMainStore("detailData", {});
                    mainStore.setMainStore("detaiDeflData", {});

                    if (paramsMatch.firstParam == "companies" && isAdd) {
                        getData(data._id);
                    }

                    setTimeout(() => navigation(url, { replace: false }), 100);
                };

                const renderSubtitle = (item, idx) => {
                    if (idx !== 0 || !paramsMatch || paramsMatch.secondParam === "add") return null;
                    const tag = getStatusTag(detailData);
                    return (
                        <div className="container__sub">
                            <TagUi severity={tag.type} value={tag.title} />
                            <TagUi value={`Код ${detailData.code}`} bgColor="primary" />
                            <TagUi value={`ID ${detailData._id}`} bgColor="gray" />
                        </div>
                    );
                };

                const renderTitle = (item) => {
                    const titleText = item?.blockName;
                    if (!titleText) return null;

                    return (
                        <span>
                            {titleText}
                            {item.blockSub && <span style={item.blockSub?.style}>{item.blockSub?.name}</span>}
                        </span>
                    );
                };

                const handleSave = async (e, additionalData = {}) => {
                    let data = { ...detailData, ...additionalData };

                    if (data && !data.dateEnd) {
                        data.dateEnd = "2125-01-01T00:00+00:00";
                    }

                    if (paramsMatch.firstParam === "projects") {
                        data.additionalProperties = { isShowcase: true }; // добавочный ключ для трафика
                    }
                    if (paramsMatch.firstParam === "offers" && data.vendorInfo?.workViaAgency) {
                        data.ord = `Реклама. Рекламодатель ${data.vendorInfo?.fullOpf || ""}, ИНН ${data.vendorInfo?.INN || ""}`;
                    }

                    if (paramsMatch.firstParam === "companies") {
                        data = modifyDataToUpdate(data); // удаляет ненужные ключи в кампаниях
                        data.dateStartCampaign = moment(new Date()).toISOString(); // ключ создания компании
                    }

                    const resp = await mainStore.postByUrl(`${apiUrl}${queryForApi ? queryForApi : ""}`, data);
                    if (resp && resp.success) {
                        mainStore.setMainStore("toast", {
                            severity: "success",
                            life: 4000,
                            summary: "Добавлено",
                            detail: "Элемент успешно добавлен",
                        });
                        if (funcAfterSave) funcAfterSave(e, resp.data); // если нужно что-то выполнить после сохранения

                        if (backEnabled) {
                            handleBack(resp.data);
                        }
                        return;
                    }
                    if ((!resp.success && resp.msg) || resp.data) {
                        mainStore.setMainStore("toast", {
                            severity: "warn",
                            life: 4000,
                            summary: "Ошибка",
                            detail: resp.msg || resp.data,
                        });
                        return;
                    }
                    mainStore.setMainStore("toast", {
                        severity: "warn",
                        life: 4000,
                        summary: "Ошибка",
                        detail: "Что-то пошло не так",
                    });
                };

                const handleClone = () => {
                    mainStore.setMainStore("modal", {
                        type: "cloneCompany",
                        offer: detailData,
                        title: "Клонирование оффера",
                    });
                };

                const getRequiredFields = (inputObj, prefix = "") => {
                    const keys = [];
                    Object.entries(inputObj).forEach(([key, val]) => {
                        const fullKey = prefix ? `${prefix}.${key}` : key;
                        if (val.required) {
                            if (val.triggerShowValue && val.triggerShow) {
                                const triggerPath = val.triggerShow;
                                const triggerVal = getValByPath(triggerPath, detailData);

                                const typeOf = Array.isArray(val.triggerShowValue) ? "array" : typeof val.triggerShowValue;

                                switch (typeOf) {
                                    case "array":
                                        if (val.triggerShowValue.includes(triggerVal)) keys.push(fullKey);
                                        break;

                                    default:
                                        if (triggerVal == val.triggerShowValue) keys.push(fullKey);
                                        break;
                                }
                            } else if (val.type !== "objectInputs") {
                                keys.push(fullKey);
                            }
                        }
                        if (val.inputs) keys.push(...getRequiredFields(val.inputs, fullKey));
                    });

                    return keys;
                };

                const calcDisabledRequired = useCallback((currentDetailData, currentInputs) => {
                    const excludes = {
                        "vendor.rateIds": "rateIds",
                    };

                    const reqFields = (currentInputs || [])
                        .reduce((acc, item) => {
                            let keys = [];
                            if (!item.hidden) keys = getRequiredFields(item.inputs);
                            return acc.concat(keys);
                        }, [])
                        .map((i) => excludes[i] || i);

                    const hasInvalidInputs = !!document.querySelector(".p-inputtext.p-invalid");

                    const btnDisabled = reqFields.some((path) => {
                        const val = getValByPath(path, currentDetailData);
                        return val == null || val === "" || (Array.isArray(val) && !val.length) || hasInvalidInputs;
                    });

                    return btnDisabled;
                }, []);

                const disabledUpdateRequired = useMemo(() => calcDisabledRequired(detailData, inputs), [detailData, inputs, calcDisabledRequired]);
                const inputFieldsDisable = useCallback(() => disabledUpdateRequired, [disabledUpdateRequired]);

                const handleStep = (currentStep) => {
                    const validationErrors = validateRequiredFields(inputs, detailData);

                    if (validationErrors.errors.length > 0) {
                        setErrorArr(validationErrors.errors);
                        mainStore.setMainStore("toast", {
                            severity: "error",
                            life: 10000,
                            content: () => {
                                return (
                                    <div className="flex flex-column align-items-left" style={{ flex: "1" }}>
                                        <div className="flex align-items-center gap-2">
                                            <span className="font-bold text-900">Проверьте поля:</span>
                                            {validationErrors.name.map((i, idx) => (
                                                <ul key={idx}>
                                                    <li>{i}</li>
                                                </ul>
                                            ))}
                                        </div>
                                    </div>
                                );
                            },
                        });
                    } else {
                        stepperData.disableStep[currentStep].isStep = true;
                        setStep(currentStep + 1);
                    }
                };

                const updateStep = (arr, index, newValue) => {
                    if (index >= 0 && index < arr.length) {
                        arr[index].disable = newValue;
                        if (newValue) {
                            arr.forEach((element, idx) => {
                                if (idx > index) {
                                    element.isStep = false;
                                }
                            });
                        }
                    }
                    return arr;
                };

                const handleSaveAndUpdate = (e, draft = false) => {
                    const validationErrors = validateRequiredFields(inputs, detailData);

                    const showErrors = () => {
                        setErrorArr(validationErrors.errors);
                        mainStore.setMainStore("toast", {
                            severity: "error",
                            life: 10000,
                            content: () => {
                                return (
                                    <div className="flex flex-column align-items-left" style={{ flex: "1" }}>
                                        <div className="flex align-items-center gap-2">
                                            <span className="font-bold text-900">Проверьте поля:</span>
                                            {validationErrors.name.map((i, idx) => (
                                                <ul key={idx}>
                                                    <li>{i}</li>
                                                </ul>
                                            ))}
                                        </div>
                                    </div>
                                );
                            },
                        });
                    };

                    if (draft) {
                        if (validationErrors.errors.length > 0) {
                            showErrors();
                        } else {
                            handleSave(e, { draft: true, active: false, archived: false });
                        }
                    } else {
                        if (isAdd) {
                            if (validationErrors.errors.length > 0) {
                                showErrors();
                            } else {
                                handleSave(e, { active: true });
                            }
                        } else {
                            if (validationErrors.errors.length > 0) {
                                showErrors();
                            } else {
                                handleUpdate(isDraft ? { draft: false, active: true, archived: false } : {});
                            }
                        }
                    }
                };

                const disabledUpdataBtnStep = updateStep(stepperData.disableStep, step - 1, !hasDiff || disabledUpdateRequired);
                const disabledUpdateDiff = !hasDiff;
                const disabledUpdataBtn = disabledUpdateDiff || disabledUpdateRequired;
                const disableUpdataBtnABtest = detailData.active;

                const getButtons = () => {
                    const ctx = {
                        step,
                        countOfSteps,
                        disabledUpdateRequired,
                        disabledUpdateDiff,
                        disabledUpdataBtn,
                        disableUpdataBtnABtest,
                        handleSave,
                        handleUpdate,
                        handleClone,
                        confirmArchive,
                        confirmPause,
                        confirmPublish,
                        confirmPublishChanges,
                        confirmPublishUpdata,
                        confirmPublishUpdataChanges,
                        confirmEridGenerate,
                        eridToRegenerate,
                    };

                    const config = btnsConfig[paramsMatch.firstParam];
                    if (!config) return [];

                    if (isAdd && config.isAdd) return config.isAdd(ctx);
                    if (isDraft && config.isDraft) return config.isDraft(ctx);
                    if (isActive && config.isActive) return config.isActive(ctx);
                    if (isArchived && config.isArchived) return config.isArchived(ctx);
                    if (!isAdd && config.isInactive) return config.isInactive(ctx);

                    return [];
                };

                // console.log(diff);

                const handleButtonLinkComapnies = (item) => {
                    sessionStorage.setItem("companies_tab_active", JSON.stringify([{ label: item.name, id: item._id }]));
                    window.open("/companies", "_blank");
                };

                const valid_click = (btn) => {
                    const validationErrors = validateRequiredFields(inputs, detailData);

                    if (btn.label === "Сохранить изменения" && paramsMatch.firstParam !== "buckets-add") {
                        btn.action();
                        return;
                    }

                    if (btn.label === "Пауза" || btn.label === "Архив") {
                        btn.action();
                        return;
                    }

                    if (validationErrors.errors.length > 0) {
                        setErrorArr(validationErrors.errors);
                        mainStore.setMainStore("toast", {
                            severity: "error",
                            life: 10000,
                            content: () => {
                                return (
                                    <div className="flex flex-column align-items-left" style={{ flex: "1" }}>
                                        <div className="flex align-items-center gap-2">
                                            <span className="font-bold text-900">Проверьте поля:</span>
                                            {validationErrors.name.map((i, idx) => (
                                                <ul key={idx}>
                                                    <li>{i}</li>
                                                </ul>
                                            ))}
                                        </div>
                                    </div>
                                );
                            },
                        });
                    } else {
                        setErrorArr([]);
                        btn.action();
                    }
                };

                return (
                    (Object.keys(detailData).length || isAdd) && (
                        <div ref={refDetail} className="container">
                            <div className={`container__detail ${page}`}>
                                {stateInputs.map((item, idx) => {
                                    const blockInputs = item.inputs;
                                    if (item.hidden) return null;

                                    return (
                                        <CardUI key={idx} className={`detail__list ${item.className ? item.className : ""}`} title={renderTitle(item)} subTitle={renderSubtitle(item, idx)}>
                                            <div className="detail__container_items">
                                                {Object.keys(blockInputs).map((key, index) => {
                                                    if (errorSet.has(key)) {
                                                        blockInputs[key].error = true;
                                                    } else {
                                                        blockInputs[key].error = false;
                                                    }

                                                    const val = detailData && key in detailData ? detailData[key] : "";

                                                    return (
                                                        <CreateNode
                                                            changeableData={detailData}
                                                            key={index}
                                                            fullData={detaiDeflData}
                                                            keyTo={key}
                                                            item={blockInputs[key]}
                                                            value={val}
                                                            setData={setStateData}
                                                            setDataMulti={setStateDataMulti}
                                                            isAdd={false}
                                                            dataId={[key]}
                                                            disabled={false}
                                                            siteId={siteCode}
                                                            className={blockInputs[key].className || ""}
                                                            urlUpload={dataKey}
                                                            setStateInputs={setStateInputs}
                                                            stateInputs={stateInputs}
                                                            currentIdx={idx}
                                                            inputFieldsDisable={inputFieldsDisable}
                                                            isScroll={isScroll}
                                                            errorArr={errorArr}
                                                            getButtons={getButtons}
                                                            getDetailData={getData}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </CardUI>
                                    );
                                })}

                                {paramsMatch.firstParam === "projects" && (
                                    <ButtonUI className="button__link_comapnies" onClick={() => handleButtonLinkComapnies(detailData)} label="Посмотреть офферы витрины" style={{ minHeight: "50px" }} iconPos="right" icon="pi pi-arrow-up-right" />
                                )}

                                {setStep && step !== countOfSteps && <ButtonUI label="Далее" onClick={() => handleStep(step)} style={{ maxWidth: "150px", minHeight: "50px" }} severity="success" />}

                                {abTest && <AbVariants data={detailData} inputs={inputs[0]} setStateData={setStateData} prePath={["data"]} setDataMulti={setStateDataMulti} dataKey={dataKey} type={paramsMatch.firstParam} />}

                                {!(paramsMatch.firstParam === "buckets-add" && paramsMatch.secondParam === "add") && (
                                    <div className="detail__btns">
                                        {isCompanyAdd && <StepIndicator steps={stepperData.stepsArr} activeStep={step} setStep={setStep} disabledUpdataBtn={disabledUpdataBtnStep} />}

                                        <div className="detail__btns-left">{!isCompanyAdd && <ButtonUI label="Назад" severity="info" outlined icon="pi pi-chevron-left" onClick={handleBack} />}</div>

                                        {btnsParams.includes(paramsMatch.firstParam) ? (
                                            <div className="detail__btns-right">
                                                {getButtons().map((btn) => {
                                                    if (paramsMatch.firstParam === "test-offer" && disableButtonPublish && btn.label == "Запустить") {
                                                        return;
                                                    }

                                                    // if (btn.visible) return;
                                                    return <ButtonUI key={btn.label} label={btn.label} icon={btn.icon} disabled={btn.disabled} outlined severity={btn.severity} onClick={() => valid_click(btn)} />;
                                                })}
                                            </div>
                                        ) : (
                                            <div className="detail__btns-right">
                                                <ButtonUI label="Опубликовать" icon="pi pi-check" outlined severity="success" onClick={handleSaveAndUpdate} />
                                                {(isDraft || isAdd) && <ButtonUI label="Черновик" severity="secondary" outlined icon="pi pi-bookmark" onClick={confirmDraft} />}
                                                {!isAdd && !isDraft && (
                                                    <>
                                                        <ButtonUI label={detailData.active ? "Пауза" : "Снять с паузы"} severity="warning" outlined icon="pi pi-pause" onClick={confirmPause} />
                                                        {!detailData.archived && <ButtonUI label="Архив" severity="danger" outlined icon="pi pi-inbox" onClick={confirmArchive} />}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                );
            },
        ),
    ),
);

const modifyAfterGetData = (data, paramsMatch, mainStore) => {
    const respDataSTR = JSON.stringify(data);
    const respData = JSON.parse(respDataSTR);

    if (paramsMatch.firstParam === "companies") {
        const isVerifiedTelNum = respData.inputFields && respData.inputFields.some((s) => s.type === "phone" && s.isVerified);
        const radioApi = respData?.apiSendingIds?.length ? true : false;
        const typeOffer = respData?.offerTypeCPC === "cpc" ? "cpc" : !!respData?.surveyId;
        const switchKey = respData?.mailOptions?.mailActivationLink ? true : false;
        const dateEndSwitch = respData.dateEnd?.includes("2125-01-01T00:00:00") ? false : true;
        // раскоментировать когда будем заливать на стейдж
        const switchGeoTarget = respData.geoObject && Object.keys(respData.geoObject).length ? true : false;
        // const switchGeoTarget = respData.regionIds?.length || respData.cityIds?.length || respData.metroStationIds?.length ? true : false;
        const switchWorkTime = respData.workLimitations?.length || respData.workLimitationsStartTime || respData.workLimitationsEndTime ? true : false;

        const brakingСoefficient = respData.brakingСoefficient ? respData.brakingСoefficient : 1;
        // const switchDeviceTarget = respData.deviceTargetModel?.length ? true : false;
        const offerSites = {};

        mainStore.setMainStore("detaiDeflData", {
            ...respData,
            isVerifiedTelNum,
            radioApi,
            switchKey,
            typeOffer,
            dateEndSwitch,
            switchGeoTarget,
            offerSites,
            switchWorkTime,
            brakingСoefficient,
        });
        mainStore.setMainStore("detailData", {
            ...data,
            isVerifiedTelNum,
            radioApi,
            switchKey,
            typeOffer,
            dateEndSwitch,
            switchGeoTarget,
            offerSites,
            switchWorkTime,
            brakingСoefficient,
        });
    } else {
        mainStore.setMainStore("detaiDeflData", respData);
        mainStore.setMainStore("detailData", data);
    }
};

const modifyDataToUpdate = (data) => {
    const dataSTR = JSON.stringify(data);
    const newData = JSON.parse(dataSTR);

    delete newData.isVerifiedTelNum;
    delete newData.radioApi;
    delete newData.switchKey;
    delete newData.typeOffer;
    delete newData.vendor;
    delete newData.formTemplate;
    delete newData.dateEndSwitch;

    if (!Array.isArray(newData.apiSendings)) {
        newData.apiSendings = [];
    }

    return newData;
};

export default DefaultDetail;

const btnsParams = ["companies", "offers", "projects", "buckets-add", "test-offer", "test-site", "replace-data"];
